import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ReferenceDataLabels } from '../../../../../lib/constants/reference-data-labels';
import { ReferenceDataOptionLists } from '../../../../../lib/constants/reference-data-option-lists';
import { Applicant } from '../../../models/applicant.model';
import { MortgageApplication } from '../../../models/mortgage-application.model';
import { OutstandingDebt } from '../../../models/outstanding-debt.model';
import { ReferenceDataService } from './../../../../../lib/services/reference-data.service';

@Component({
  selector: 'boi-applicant-outgoing',
  templateUrl: './applicant-outgoing.component.html',
  styleUrls: ['./applicant-outgoing.component.scss']
})
export class ApplicantOutgoingComponent implements OnChanges {
  @Input() public index: number;
  @Input() public outgoing: OutstandingDebt;
  @Input() public mortgageApplication: MortgageApplication;

  public referenceDataLabels = ReferenceDataLabels;
  public referenceDataOptionLists = ReferenceDataOptionLists;
  public disabledCheckboxIndex: number;
  public hasAmountValue: boolean;
  public hasAmountAndDescriptionValue: boolean;
  public hasCreditLimit: boolean;
  public hasOverDraftLimit: boolean;

  constructor(private referenceDataService: ReferenceDataService) {}

  public get validationApplicantIndex(): number {
    return this.outgoing.ApplicantOrderIds[0] - 1;
  }
  public get validationOutstandingIndex(): number {
    const applicantIndex = this.validationApplicantIndex;
    const applicant = this.mortgageApplication.Applicants[applicantIndex];

    return applicant && applicant.OutstandingDebts ? applicant.OutstandingDebts.indexOf(this.outgoing) : this.index;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.outgoing) {
      this.cacheAttributeValues();
    }
  }

  public isOutgoingApplyedToApplicant(applicant: Applicant): boolean {
    return this.outgoing.ApplicantOrderIds.some(
      (applicantIndex) => applicantIndex === applicant.ApplicantTransactOrder
    );
  }

  public isApplicantCheckboxDisabled(applicant: Applicant): boolean {
    return (
      this.outgoing.ApplicantOrderIds.length === 1 &&
      this.outgoing.ApplicantOrderIds[0] === applicant.ApplicantTransactOrder
    );
  }

  public applyedToSelectionChanged(isSelected: boolean, applicant: Applicant): void {
    if (isSelected) {
      this.applyOutgoing(applicant);
    } else {
      this.detachOutgoingWithMainApplicantChange(applicant);
    }
  }

  private applyOutgoing(applicant: Applicant): void {
    this.outgoing.ApplicantOrderIds.push(applicant.ApplicantTransactOrder);
    this.outgoing.ApplicantOrderIds.sort((n1, n2) => n1 - n2);
    applicant.OutstandingDebts.push(this.outgoing);
    applicant.OutstandingDebts.sort((o1, o2) => (new Date(o1.CreatedDate) > new Date(o2.CreatedDate) ? 1 : -1));
  }

  private detachOutgoing(applicant: Applicant): void {
    this.outgoing.ApplicantOrderIds.splice(
      this.outgoing.ApplicantOrderIds.indexOf(applicant.ApplicantTransactOrder),
      1
    );
    applicant.OutstandingDebts.splice(
      applicant.OutstandingDebts.indexOf(
        applicant.OutstandingDebts.find((o) => o.OutstandingDebtID === this.outgoing.OutstandingDebtID)
      ),
      1
    );
  }

  private detachOutgoingWithMainApplicantChange(applicant: Applicant): void {
    const applicantOrderIndex = this.outgoing.ApplicantOrderIds.indexOf(applicant.ApplicantTransactOrder);
    this.detachOutgoing(applicant);
    if (applicantOrderIndex === 0) {
      this.changeMainApplicant();
    }
  }

  private changeMainApplicant(): void {
    const mainApplicantWithOugoing = this.mortgageApplication.Applicants.find(
      (a) => a.ApplicantTransactOrder === this.outgoing.ApplicantOrderIds[0]
    );
    const outgoing = mainApplicantWithOugoing.OutstandingDebts.find(
      (o) => o.OutstandingDebtID === this.outgoing.OutstandingDebtID
    );
    mainApplicantWithOugoing.OutstandingDebts.splice(mainApplicantWithOugoing.OutstandingDebts.indexOf(outgoing), 1);
    mainApplicantWithOugoing.OutstandingDebts.push(this.outgoing);
    mainApplicantWithOugoing.OutstandingDebts.sort((o1, o2) =>
      new Date(o1.CreatedDate) > new Date(o2.CreatedDate) ? 1 : -1
    );
  }

  public primarilyResponsibleSelectionChanged(applicant: Applicant): void {
    this.mortgageApplication.Applicants.filter((a) =>
      a.OutstandingDebts.some((o) => o.OutstandingDebtID === this.outgoing.OutstandingDebtID)
    ).forEach((a) => this.detachOutgoing(a));
    this.applyOutgoing(applicant);
  }

  private cacheAttributeValues(): void {
    if (!this.outgoing) {
      return;
    }

    const attributes = this.getAttributes(this.outgoing);
    const relevantAttributes = attributes.filter(
      (attr) => attr.Name === this.referenceDataOptionLists.AttributeNameType
    );

    this.hasAmountValue = this.hasAttributeValue(
      relevantAttributes,
      this.referenceDataOptionLists.AttributeValueAmount
    );
    this.hasAmountAndDescriptionValue = this.hasAttributeValue(
      relevantAttributes,
      this.referenceDataOptionLists.AttributeValueAmountAndDescription
    );
    this.hasCreditLimit = this.hasAttributeValue(relevantAttributes, this.referenceDataOptionLists.CreditLimit);
    this.hasOverDraftLimit = this.hasAttributeValue(relevantAttributes, this.referenceDataOptionLists.Overdraft);
  }

  private getAttributes(outgoing: OutstandingDebt): any[] {
    const optionListItem = this.referenceDataService.getOptionListConfigItemByCode(
      this.referenceDataOptionLists.DebtType,
      outgoing.DebtOutgoingTypeCode
    );

    return optionListItem?.Attributes;
  }

  private hasAttributeValue(attributes, value): boolean {
    if (!Array.isArray(attributes)) return false;
    return attributes.some((attr) => attr.Values.includes(value));
  }
}
